import { useContext } from 'react';

import {
  AuthCard,
  AuthCardHeader,
  AuthShell,
  CardFooter,
  Field,
  GhostLink,
  Input,
  SubmitButton,
} from '../../components/AuthShell';
import { PasswordToggle } from '../../components/AuthShell/PasswordToggle';
import Snackbar from '../../components/Snackbar';
import { WhiteCircularProgress } from '../../components/UI/Typography';
import { Constants } from '../../constants/login';
import { GlobalContext } from '../../context/global';
import useLogin from './useLogin';

interface LoginProps {
  /** Tenant (bank/company) logo from `login-layout`; omitted for the default brand. */
  tenantLogo?: string;
}

export function Login({ tenantLogo }: LoginProps): JSX.Element {
  const {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    toggleShowPassword,
    submitLoading,
    statusLoading,
    loginUnavailable,
    authSourceNote,
    handleSubmit,
    goToForgotPassword,
  } = useLogin();

  const { openSnackbar } = useContext(GlobalContext);

  if (loginUnavailable) {
    return (
      <AuthShell>
        <AuthCard as="div">
          <AuthCardHeader
            title={Constants.loginUnavailable}
            subtitle={Constants.loginUnavailableHint}
            tenantLogo={tenantLogo}
          />
          <CardFooter>{Constants.footer}</CardFooter>
          <CardFooter>{authSourceNote}</CardFooter>
        </AuthCard>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <AuthCard id="login" onSubmit={handleSubmit} noValidate>
        <AuthCardHeader
          title={Constants.title}
          subtitle={Constants.subtitle}
          tenantLogo={tenantLogo}
        />

        <Field>
          <label htmlFor="email">{Constants.emailLabel}</label>
          <Input
            required
            id="email"
            type="email"
            autoComplete="username"
            placeholder={Constants.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
          />
        </Field>

        <Field>
          <label htmlFor="password">{Constants.passwordLabel}</label>
          <Input
            required
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder={Constants.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: (
                <PasswordToggle
                  visible={showPassword}
                  onToggle={toggleShowPassword}
                  showLabel={Constants.showPassword}
                  hideLabel={Constants.hidePassword}
                />
              ),
            }}
          />
        </Field>

        <SubmitButton
          form="login"
          type="submit"
          size="large"
          disabled={submitLoading || statusLoading}
        >
          {submitLoading ? (
            <WhiteCircularProgress size={18} sx={{ color: 'inherit' }} />
          ) : (
            Constants.submit
          )}
        </SubmitButton>

        <GhostLink disableTouchRipple onClick={goToForgotPassword}>
          {Constants.forgotPassword}
        </GhostLink>

        <CardFooter>{Constants.footer}</CardFooter>
        <CardFooter>{authSourceNote}</CardFooter>
      </AuthCard>
      {openSnackbar && <Snackbar />}
    </AuthShell>
  );
}
